import { Link } from "react-router-dom";

import { formatPriceEur } from "@/lib/format";
import type { Room } from "@/types/api";

interface RoomCardProps {
  room: Room;
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function deriveFeatures(room: Room): string[] {
  const out: string[] = [];
  if (room.capacity) out.push(`Bis zu ${room.capacity} Personen`);
  if (room.location) out.push(`Standort ${room.location}`);
  out.push(`Ab ${room.min_duration_minutes} Min. buchbar`);
  return out.slice(0, 3);
}

export function RoomCard({ room }: RoomCardProps) {
  const hero = room.images[0];
  const features = deriveFeatures(room);
  // Erstes kostenpflichtiges Angebot als Headline, Rest als Kurzzeile.
  const headline = room.prices.find((p) => p.price_cents > 0) ?? room.prices[0];
  const rest = room.prices.filter((p) => p !== headline);

  return (
    <Link
      to={`/raum/${room.slug}`}
      aria-label={`Verfügbarkeit für ${room.name} prüfen`}
      className="group flex flex-col overflow-hidden rounded-card border border-line bg-surface shadow-card transition-all duration-200 ease-smooth hover:-translate-y-0.5 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <div
        className="relative aspect-[16/10] w-full overflow-hidden bg-surface-warm"
        style={{ backgroundColor: room.color }}
      >
        {hero ? (
          <img
            src={hero}
            alt={`Tanzraum ${room.name}${room.location ? `, ${room.location}` : ""}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.05]"
          />
        ) : (
          <div className="flex h-full items-center justify-center font-display text-h2 uppercase text-white/80">
            {room.name}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          <h3 className="font-display text-h3 uppercase text-primary">{room.name}</h3>
          {room.capacity && (
            <span className="shrink-0 rounded bg-surface-warm px-2 py-1 text-caption font-medium uppercase tracking-wider text-primary">
              {room.capacity}&nbsp;Pl.
            </span>
          )}
        </div>

        {room.description && (
          <p className="mb-5 line-clamp-2 text-body text-ink-soft">{room.description}</p>
        )}

        <ul className="mb-6 space-y-2">
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-2 text-body-sm text-ink">
              <CheckIcon className="shrink-0 text-primary" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mt-auto flex items-end justify-between border-t border-line pt-4">
          <div>
            {headline && (
              <>
                <span className="block text-caption uppercase tracking-wider text-ink-muted">
                  {headline.name} ab
                </span>
                <span className="font-display text-h3 text-primary">
                  {headline.price_cents === 0 ? (
                    "kostenlos"
                  ) : (
                    <>
                      {formatPriceEur(headline.price)}
                      <span className="ml-1 text-body-sm text-ink-muted">/ h</span>
                    </>
                  )}
                </span>
              </>
            )}
            {rest.length > 0 && (
              <span className="block text-caption text-ink-muted">
                {rest
                  .map((p) =>
                    p.price_cents === 0
                      ? `${p.name} kostenlos`
                      : `${p.name} ${formatPriceEur(p.price)}/h`,
                  )
                  .join(" · ")}
              </span>
            )}
          </div>
          <span className="inline-flex items-center rounded bg-primary px-4 py-2 text-body-sm font-semibold text-white transition-colors group-hover:bg-primary-dark">
            Verfügbarkeit prüfen
          </span>
        </div>
      </div>
    </Link>
  );
}
